/**
 * Helper for TinyMCE component.
 * This file is based on utility.js from CI app. (apps/assets/js/utility.js)
 *
 * Sample usage:
 * (import only what we need):
 * import { toolbar, plugins } from "@/module/commons/helper-tinymce";
 *
 * or
 * (import the whole helper):
 * import tinymceHelper from "@/module/commons/helper-tinymce";
 *
 * Ignored:
 * setup > editor.on('init') method
 *
 * Need to revisit:
 * external_plugins
 */
import Axios from "axios";
import { DateTime } from "luxon";
import store from "@/store/index";

// prettier-ignore
export const core = {
    branding: false,
    menubar: false,
    contextmenu: false,
    cache_suffix: "v5.4.2",
    convert_urls: false,
    content_css: "/view/tinymce/vetter-styles.css?" + Date.now(),
    table_advtab: false,
    convert_newlines_to_brs: false,
    default_link_target: "_blank",
    anchor_top: false,
    anchor_bottom: false,
    browser_spellcheck: true,
    target_list: false,
    pagebreak_separator: '<div class="pagebreak" pagebreak="true">',
    paste_webkit_styles: "text-align text-decoration color background-color, list-style-type, width, height",
    paste_retain_style_properties: "text-align text-decoration color background-color, list-style-type, width, height",
    paste_word_valid_elements: "h1,h2,h3,h4,h5,h6,p,pre,span",
    paste_data_images: false,
    br_in_pre: false,
    statusbar: false,
    imagetools_proxy: "/apps/index.php/ajax/utility/tinymce_proxy",
    fontsize_formats: '8px 9px 10px 11px 12px 14px 16px 18px 20px 24px 26px 28px 36px 48px 72px',  // Define available font sizes
    content_style: "@import url('https://fonts.googleapis.com/css2?family=Noto+Sans&display=swap');" +
                 "@import url('https://fonts.googleapis.com/css2?family=Open+Sans&display=swap');" +
                 "@import url('https://fonts.googleapis.com/css2?family=Roboto&display=swap');" +
                 "@import url('https://fonts.googleapis.com/css2?family=PT+Sans&display=swap');" +
                 "@import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville&display=swap');" +
                 "@import url('https://fonts.googleapis.com/css2?family=Oswald&display=swap');" +
                 "@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk&display=swap');" + 
                 "@import url('https://fonts.googleapis.com/css2?family=Space+Mono&display=swap');",
    font_formats:'Verdana=verdana,arial,helvetica,sans-serif;' +
                 'Noto Sans=noto sans,segoe ui,sans-serif;' +
                 'Open Sans=open sans,calibri,sans-serif;' +
                 'Roboto=roboto,arial,sans-serif;' +
                 'PT Sans=pt sans,verdana,sans-serif;' +
                 'Libre Baskerville=libre baskerville,georgia,serif;' +
                 'Oswald=oswald,impact,sans-serif;' +
                 'Space Grotesk=space grotesk,trebuchet ms,sans-serif;' +
                 'Space Mono=space mono,consolas,monospace;',
    table_default_attributes: {
        cellpadding: 20,
        border: 0,
        cellspacing: 0
    },
    table_default_styles: {
        width: "100%",
        "border-color": "#e1e1e1"
    },
    table_cell_class_list: [
        { title: "(Border)", value: "" },
        { title: "Solid", value: "bordered" },
        { title: "Solid (bottom)", value: "borderedbottom" }
    ],
    style_formats: [
        {
            title: "Headers",
            items: [
                { title: "Header 1", format: "h1" },
                { title: "Header 2", format: "h2" },
                { title: "Header 3", format: "h3" },
                { title: "Header 4", format: "h4" },
                { title: "Header 5", format: "h5" },
                { title: "Header 6", format: "h6" }
            ]
        },
        {
            title: "Inline",
            items: [
                { title: "Underline", icon: "underline", format: "underline" },
                {
                    title: "Strikethrough",
                    icon: "strike-through",
                    format: "strikethrough"
                },
                {
                    title: "Superscript",
                    icon: "superscript",
                    format: "superscript"
                },
                { title: "Subscript", icon: "subscript", format: "subscript" },
                {
                    title: "Code",
                    icon: "sourcecode",
                    inline: "span",
                    classes: "code"
                }
            ]
        },
        {
            title: "Blocks",
            items: [
                { title: "Paragraph", format: "p" },
                { title: "Blockquote", format: "blockquote" },
                { title: "Pre", format: "pre" }
            ]
        },
        {
            title: "Alignment",
            items: [
                { title: "Left", icon: "align-left", format: "alignleft" },
                {
                    title: "Center",
                    icon: "align-center",
                    format: "aligncenter"
                },
                { title: "Right", icon: "align-right", format: "alignright" },
                {
                    title: "Justify",
                    icon: "align-justify",
                    format: "alignjustify"
                }
            ]
        }
    ],
    charmap: [
        ["160", "no-break space"],
        ["173", "soft hyphen"],
        // finance
        ["162", "cent sign"],
        ["8364", "euro sign"],
        ["163", "pound sign"],
        ["165", "yen sign"],
        // signs
        ["169", "copyright sign"],
        ["174", "registered sign"],
        ["8482", "trade mark sign"],
        ["8240", "per mille sign"],
        ["181", "micro sign"],
        ["183", "middle dot"],
        ["8226", "bullet"],
        ["8230", "three dot leader"],
        ["8242", "minutes / feet"],
        ["8243", "seconds / inches"],
        ["167", "section sign"],
        ["182", "paragraph sign"],
        ["223", "sharp s / ess-zed"],
        // quotations
        ["8249", "single left-pointing angle quotation mark"],
        ["8250", "single right-pointing angle quotation mark"],
        ["171", "left pointing guillemet"],
        ["187", "right pointing guillemet"],
        ["8216", "left single quotation mark"],
        ["8217", "right single quotation mark"],
        ["8220", "left double quotation mark"],
        ["8221", "right double quotation mark"],
        ["8218", "single low-9 quotation mark"],
        ["8222", "double low-9 quotation mark"],
        ["60", "less-than sign"],
        ["62", "greater-than sign"],
        ["8804", "less-than or equal to"],
        ["8805", "greater-than or equal to"],
        ["8211", "en dash"],
        ["8212", "em dash"],
        ["175", "macron"],
        ["8254", "overline"],
        ["164", "currency sign"],
        ["166", "broken bar"],
        ["168", "diaeresis"],
        ["161", "inverted exclamation mark"],
        ["191", "turned question mark"],
        ["710", "circumflex accent"],
        ["732", "small tilde"],
        ["176", "degree sign"],
        ["8722", "minus sign"],
        ["177", "plus-minus sign"],
        ["247", "division sign"],
        ["8260", "fraction slash"],
        ["215", "multiplication sign"],
        ["185", "superscript one"],
        ["178", "superscript two"],
        ["179", "superscript three"],
        ["188", "fraction one quarter"],
        ["189", "fraction one half"],
        ["190", "fraction three quarters"],
        // math / logical
        ["402", "function / florin"],
        ["8747", "integral"],
        ["8721", "n-ary sumation"],
        ["8734", "infinity"],
        ["8730", "square root"],
        ["8764", "similar to"],
        ["8773", "approximately equal to"],
        ["8776", "almost equal to"],
        ["8800", "not equal to"],
        ["8801", "identical to"],
        ["8712", "element of"],
        ["8713", "not an element of"],
        ["8715", "contains as member"],
        ["8719", "n-ary product"],
        ["8743", "logical and"],
        ["8744", "logical or"],
        ["172", "not sign"],
        ["8745", "intersection"],
        ["8746", "union"],
        ["8706", "partial differential"],
        ["8704", "for all"],
        ["8707", "there exists"],
        ["8709", "diameter"],
        ["8711", "backward difference"],
        ["8727", "asterisk operator"],
        ["8733", "proportional to"],
        ["8736", "angle"],
        // undefined
        ["180", "acute accent"],
        ["184", "cedilla"],
        ["170", "feminine ordinal indicator"],
        ["186", "masculine ordinal indicator"],
        ["8224", "dagger"],
        ["8225", "double dagger"],
        // alphabetical special chars
        ["192", "A - grave"],
        ["193", "A - acute"],
        ["194", "A - circumflex"],
        ["195", "A - tilde"],
        ["196", "A - diaeresis"],
        ["197", "A - ring above"],
        ["256", "A - macron"],
        ["198", "ligature AE"],
        ["199", "C - cedilla"],
        ["200", "E - grave"],
        ["201", "E - acute"],
        ["202", "E - circumflex"],
        ["203", "E - diaeresis"],
        ["274", "E - macron"],
        ["204", "I - grave"],
        ["205", "I - acute"],
        ["206", "I - circumflex"],
        ["207", "I - diaeresis"],
        ["298", "I - macron"],
        ["208", "ETH"],
        ["209", "N - tilde"],
        ["210", "O - grave"],
        ["211", "O - acute"],
        ["212", "O - circumflex"],
        ["213", "O - tilde"],
        ["214", "O - diaeresis"],
        ["216", "O - slash"],
        ["332", "O - macron"],
        ["338", "ligature OE"],
        ["352", "S - caron"],
        ["217", "U - grave"],
        ["218", "U - acute"],
        ["219", "U - circumflex"],
        ["220", "U - diaeresis"],
        ["362", "U - macron"],
        ["221", "Y - acute"],
        ["376", "Y - diaeresis"],
        ["562", "Y - macron"],
        ["222", "THORN"],
        ["224", "a - grave"],
        ["225", "a - acute"],
        ["226", "a - circumflex"],
        ["227", "a - tilde"],
        ["228", "a - diaeresis"],
        ["229", "a - ring above"],
        ["257", "a - macron"],
        ["230", "ligature ae"],
        ["231", "c - cedilla"],
        ["232", "e - grave"],
        ["233", "e - acute"],
        ["234", "e - circumflex"],
        ["235", "e - diaeresis"],
        ["275", "e - macron"],
        ["236", "i - grave"],
        ["237", "i - acute"],
        ["238", "i - circumflex"],
        ["239", "i - diaeresis"],
        ["299", "i - macron"],
        ["240", "eth"],
        ["241", "n - tilde"],
        ["242", "o - grave"],
        ["243", "o - acute"],
        ["244", "o - circumflex"],
        ["245", "o - tilde"],
        ["246", "o - diaeresis"],
        ["248", "o slash"],
        ["333", "o macron"],
        ["339", "ligature oe"],
        ["353", "s - caron"],
        ["249", "u - grave"],
        ["250", "u - acute"],
        ["251", "u - circumflex"],
        ["252", "u - diaeresis"],
        ["363", "u - macron"],
        ["253", "y - acute"],
        ["254", "thorn"],
        ["255", "y - diaeresis"],
        ["563", "y - macron"],
        ["913", "Alpha"],
        ["914", "Beta"],
        ["915", "Gamma"],
        ["916", "Delta"],
        ["917", "Epsilon"],
        ["918", "Zeta"],
        ["919", "Eta"],
        ["920", "Theta"],
        ["921", "Iota"],
        ["922", "Kappa"],
        ["923", "Lambda"],
        ["924", "Mu"],
        ["925", "Nu"],
        ["926", "Xi"],
        ["927", "Omicron"],
        ["928", "Pi"],
        ["929", "Rho"],
        ["931", "Sigma"],
        ["932", "Tau"],
        ["933", "Upsilon"],
        ["934", "Phi"],
        ["935", "Chi"],
        ["936", "Psi"],
        ["937", "Omega"],
        ["945", "alpha"],
        ["946", "beta"],
        ["947", "gamma"],
        ["948", "delta"],
        ["949", "epsilon"],
        ["950", "zeta"],
        ["951", "eta"],
        ["952", "theta"],
        ["953", "iota"],
        ["954", "kappa"],
        ["955", "lambda"],
        ["956", "mu"],
        ["957", "nu"],
        ["958", "xi"],
        ["959", "omicron"],
        ["960", "pi"],
        ["961", "rho"],
        ["962", "final sigma"],
        ["963", "sigma"],
        ["964", "tau"],
        ["965", "upsilon"],
        ["966", "phi"],
        ["967", "chi"],
        ["968", "psi"],
        ["969", "omega"],
        // symbols
        ["8501", "alef symbol"],
        ["982", "pi symbol"],
        ["8476", "real part symbol"],
        ["978", "upsilon - hook symbol"],
        ["8472", "Weierstrass p"],
        ["8465", "imaginary part"],
        // arrows
        ["8592", "leftwards arrow"],
        ["8593", "upwards arrow"],
        ["8594", "rightwards arrow"],
        ["8595", "downwards arrow"],
        ["8596", "left right arrow"],
        ["8629", "carriage return"],
        ["8656", "leftwards double arrow"],
        ["8657", "upwards double arrow"],
        ["8658", "rightwards double arrow"],
        ["8659", "downwards double arrow"],
        ["8660", "left right double arrow"],
        ["8756", "therefore"],
        ["8834", "subset of"],
        ["8835", "superset of"],
        ["8836", "not a subset of"],
        ["8838", "subset of or equal to"],
        ["8839", "superset of or equal to"],
        ["8853", "circled plus"],
        ["8855", "circled times"],
        ["8869", "perpendicular"],
        ["8901", "dot operator"],
        ["8968", "left ceiling"],
        ["8969", "right ceiling"],
        ["8970", "left floor"],
        ["8971", "right floor"],
        ["9001", "left-pointing angle bracket"],
        ["9002", "right-pointing angle bracket"],
        ["9674", "lozenge"],
        ["9824", "black spade suit"],
        ["9827", "black club suit"],
        ["9829", "black heart suit"],
        ["9830", "black diamond suit"],
        ["8194", "en space"],
        ["8195", "em space"],
        ["8201", "thin space"],
        ["8204", "zero width non-joiner"],
        ["8205", "zero width joiner"],
        ["8206", "left-to-right mark"],
        ["8207", "right-to-left mark"]
    ],
    setup: setup,
    init_instance_callback: init_instance_callback
};

// prettier-ignore
export const toolbar = {
    communication: "styleselect | insert |  bold italic | forecolor backcolor | bullist",
    default: "undo redo | styleselect | fontselect | fontsizeselect | insert |  bold italic | alignleft aligncenter alignright alignjustify ltr rtl | forecolor backcolor | bullist numlist outdent indent | table code"
};

export const plugins = [
    "advlist autolink lists link charmap print hr pagebreak",
    "searchreplace visualblocks code", // "textcolor" "colorpicker" is now removed (already built in to the core editor)
    "insertdatetime table paste nonbreaking toc directionality"
];

export default {
    core,
    toolbar,
    plugins
};

function setup(editor) {
    editor.on("keyup", e => {
        // eslint-disable-next-line no-undef
        tinyMCE.triggerSave();

        const timezone = store.state.setting.timezone ?? this._timeZone;
        const content = editor.getContent();
        if (content.includes("#date") || content.includes("#time")) {
            if (content.includes("#date")) {
                // if '#date' is typed, replace '#date' with current date time
                const formatString = getLuxonFormat() + " hh:mm:ss a";
                const timestamp = DateTime.now()
                    .setZone(timezone)
                    .toFormat(formatString);

                let initials = localStorage.getItem("my_staff_initials") || "";
                if (initials !== "") {
                    initials = ` (${initials})`;
                }

                const value = `<p style="text-align: justify;">${timestamp}${initials}</p>`;

                const updatedContent = content.replace(/#date/g, value);
                editor.setContent(updatedContent);
            }

            // if '#time' is typed, replace '#date' with current time
            if (content.includes("#time")) {
                // Replace '#time' with the current timestamp
                const timestamp = DateTime.now()
                    .setZone(timezone)
                    .toFormat("hh:mm a");
                const value = `<p style="text-align: justify;">${timestamp}</p>`;

                const updatedContent = content.replace(/#time/g, value);
                editor.setContent(updatedContent);
            }
            const spans = editor.getBody().querySelectorAll("p[style]");
            if (spans.length > 0) {
                const lastSpan = spans[spans.length - 1];
                editor.selection.select(lastSpan);
                editor.selection.collapse(false); // Collapse to move the cursor after the inserted span
            }

            editor.focus();
        }
    });

    editor.ui.registry.addMenuButton("insert", {
        icon: "plus",
        tooltip: "Insert",
        fetch: function (callback) {
            callback(
                "link | charmap hr pagebreak insertdatetime nonbreaking toc"
            );
        }
    });

    /* An autocompleter that allows you to insert special characters */
    editor.ui.registry.addAutocompleter("specialchars", {
        ch: "#",
        minChars: 1,
        columns: 1,
        fetch: async pattern => {
            try {
                const res = await Axios.get(
                    `${process.env.BARRAMUNDI}account/template/snippet?filter_hashtag=1&isTinymce=1`
                );

                let snippets = res.data.response.resource.data;
                let results = snippets
                    .filter(object => {
                        return object.hashtag.indexOf(pattern) !== -1;
                    })
                    .map(object => {
                        return {
                            value: object.elements,
                            text: object.hashtag
                        };
                    });

                return results;
            } catch (e) {
                return [];
            }
        },
        onAction: function (autocompleteApi, rng, value) {
            editor.selection.setRng(rng);
            value = value ? value.replace(/&nbsp;/g, " ") : value;
            value = '<p style="text-align: justify;">' + value + "</p>";
            editor.insertContent(value);
            autocompleteApi.hide();
        }
    });
}

function init_instance_callback(editor) {
    editor.on("GetContent", e => {
        let content = e.content;
        let div = document.createElement("div");
        div.innerHTML = content;

        for (const anchor of div.querySelectorAll("a[href]")) {
            let link = anchor.getAttribute("href");
            link =
                link.indexOf("://") === -1 && link[0] != "#"
                    ? "http://" + link
                    : link;

            if (!valid_url(link)) {
                link = "#";
            }

            anchor.setAttribute("href", link);
        }

        for (const caption of div.querySelectorAll("table caption")) {
            let parent = caption.parentNode;
            let paragraph = document.createElement("p");
            paragraph.style = "text-align:center;margin:0";
            paragraph.className = "caption";
            paragraph.innerHTML = caption.innerHTML;
            paragraph.insertBefore(parent);
            parent.removeChild(caption);
        }

        div.querySelectorAll("table[border]").forEach(border => {
            if (border.getAttribute("border") > 0) {
                border.style.borderColor = "#e1e1e1";
                border.className = "haveborder";
            }
        });

        div.querySelectorAll("table tr").forEach(tr => {
            let total = 0;
            tr.querySelectorAll("td").forEach(tds => {
                tds.querySelectorAll("td").forEach(td => {
                    total += td.clientWidth;
                });
            });

            tr.querySelectorAll("td").forEach(tds => {
                tds.querySelectorAll("td").forEach(td => {
                    let width = td.clientWidth;
                    total += width;

                    if (width) {
                        td.style.width = `${(width / total) * 100}%`;
                    }
                });
            });
        });

        div.querySelectorAll("table").forEach(table => {
            let cellspacing = table.getAttribute("cellspacing");
            // table.setAttribute("cellspacing", `${cellspacing * 2}`); // times 2 to match pdf's DPI.
            let width = table.clientWidth;
            if (width) {
                table.style.width = `${(width * 12) / 14}pt`; // times 2 to match pdf's DPI.
            }
        });

        div.querySelectorAll("span[style]").forEach(span => {
            span.style.textAlign = "";
        });

        e.content = div.innerHTML;
    });
}

function valid_url(url) {
    var re_weburl = new RegExp(
        "^" +
            // protocol identifier
            "(?:(?:https?|ftp)://)" +
            // user:pass authentication
            "(?:\\S+(?::\\S*)?@)?" +
            "(?:" +
            // IP address exclusion
            // private & local networks
            "(?!(?:10|127)(?:\\.\\d{1,3}){3})" +
            "(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})" +
            "(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})" +
            // IP address dotted notation octets
            // excludes loopback network 0.0.0.0
            // excludes reserved space >= 224.0.0.0
            // excludes network & broacast addresses
            // (first & last IP address of each class)
            "(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])" +
            "(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}" +
            "(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))" +
            "|" +
            // host name
            "(?:(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)" +
            // domain name
            "(?:\\.(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)*" +
            // TLD identifier
            "(?:\\.(?:[a-z\\u00a1-\\uffff]{2,}))" +
            // TLD may end with dot
            "\\.?" +
            ")" +
            // port number
            "(?::\\d{2,5})?" +
            // resource path
            "(?:[/?#]\\S*)?" +
            "$",
        "i"
    );
    return re_weburl.test(url);
}

const SYS_DATEFORMAT = {
    MMDDYYYY: "MM/DD/YYYY",
    DDMMYYYY: "DD/MM/YYYY",
    YYYYMMDD: "YYYY/MM/DD",
    MDDYYYY: "M DD, YYYY",
    DDMYYYY: "DD M YYYY",
    DD_M_YYYY: "DD-M-YYYY"
};

function getLuxonFormat(format) {
    let oFmt = format ? format : store.state.setting.date_format;

    switch (oFmt) {
        case SYS_DATEFORMAT.MMDDYYYY:
            return "MM/dd/yyyy";
        case SYS_DATEFORMAT.DDMMYYYY:
            return "dd/MM/yyyy";
        case SYS_DATEFORMAT.YYYYMMDD:
            return "yyyy/LL/dd";
        case SYS_DATEFORMAT.MDDYYYY:
            return "MMM dd, yyyy";
        case SYS_DATEFORMAT.DDMYYYY:
            return "dd MMM yyyy";
        case SYS_DATEFORMAT.DD_M_YYYY:
            return "dd-MMM-yyyy";
        default:
            return "MMM dd, yyyy";
    }
}
